/**
 * Tippy.js Initialization
 * 
 * Centralizes Tippy.js tooltip library setup and configuration.
 * Tippy is used throughout the application for tooltips on:
 * - Tracking numbers (copy-to-clipboard feedback)
 * - Order numbers (copy-to-clipboard feedback)
 * - Travel time indicators
 * - Last updated timestamps
 * - Notes icons (lazy-loaded note previews)
 * - Action buttons (refresh, bookmark, navigation)
 * - Filter toggles
 * - Package status indicators
 * 
 * @see https://atomiks.github.io/tippyjs/
 */

import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css'; // Import default Tippy styles
import type { Instance, Props } from 'tippy.js';

// Re-export tippy for use throughout the app
export { tippy };

// Export type for Tippy instances (for TypeScript consumers)
// Use the actual Instance type from tippy.js, not ReturnType
export type TippyInstance = Instance<Props>;

/**
 * Default Tippy configuration used across the app
 */
export const defaultTippyConfig = {
  placement: 'top' as const,
  arrow: true,
  animation: 'fade' as const,
  duration: [200, 150] as [number, number],
};

/**
 * Configuration for copy-to-clipboard tooltips
 * Uses manual trigger so we can control show/hide programmatically
 */
export const clipboardTippyConfig = {
  ...defaultTippyConfig,
  content: 'Click to Copy',
  trigger: 'manual' as const,
};

/**
 * Configuration for tooltips with HTML content
 */
export const htmlTippyConfig = {
  ...defaultTippyConfig,
  allowHTML: true,
};

/**
 * Configuration for lazy-loaded tooltips (e.g., notes preview)
 * Content is fetched on first show, not on initialization
 */
export const lazyTippyConfig = {
  ...defaultTippyConfig,
  allowHTML: true,
  interactive: true,
  maxWidth: 350,
};
